"use client";

/**
 * The newest published release (GET /api/releases/latest) and this
 * browser's "seen" marker, shared by every "something new happened"
 * surface: the dot on the header's "What's new" entry, on the phone
 * More sheet's row and on the tab bar's More button. (The one-time corner
 * card went on 2026-10-18: it covered content; the dot is the announcer.)
 *
 * Seen marker: localStorage "menzil.rel.seen" holds the id of the last
 * release this browser has seen (opened the release page or the
 * changelog with it on top). Same for signed-in and signed-out visitors;
 * no server state.
 *
 * Nothing is news to someone who was not here before it (owner,
 * 2026-10-08: "you sure that new users won't face it?"):
 * - a browser on its first visit (no seen marker and no trace of an
 *   earlier visit: cookie answer, install-sheet dismissal, or the
 *   "menzil.visited" marker set here on every load) silently records
 *   the current release as seen, so only releases published after the
 *   first visit ever announce themselves;
 * - a signed-in account created after the release was published is
 *   told nothing about it (predatesAccount), whatever the browser says.
 *
 * Everything degrades to nothing: no API (404 until the backend ships),
 * no storage, no JS errors, just no dot and no card. One module-level
 * store, so marking seen clears the dot everywhere at once.
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { API_BASE } from "@/lib/api";
import { useLocale } from "@/lib/locale-context";
import { NEW_MARK_DAYS, ageDays, toReleaseCard, type ReleaseCard } from "@/lib/announcements";

const SEEN_KEY = "menzil.rel.seen";
/** A release older than this is history, not news: no dot for it (a week
 *  since the quiet UI, 2026-10-29; it was 30 days). */
const MAX_AGE_DAYS = NEW_MARK_DAYS;
const VISITED_KEY = "menzil.visited";
/** Any of these in storage means the browser has been here before. */
const PRIOR_VISIT_KEYS = ["menzil-cookie-consent", "menzil-install-firstvisit-dismissed", VISITED_KEY];

interface Snapshot {
  latest: ReleaseCard | null;
  /** The latest-release request has settled (either way). */
  ready: boolean;
  seen: string | null;
  /** This page load is the browser's first visit: whatever release is
   *  current gets recorded as seen without being shown. */
  firstVisit: boolean;
}

let snapshot: Snapshot = { latest: null, ready: false, seen: null, firstVisit: false };
const SERVER_SNAPSHOT: Snapshot = snapshot;
let seenLoaded = false;
let loadedLocale: string | null = null;
const listeners = new Set<() => void>();

function emit(next: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function priorVisit(): boolean {
  try {
    return PRIOR_VISIT_KEYS.some((k) => localStorage.getItem(k) !== null);
  } catch {
    return false;
  }
}

function markVisited() {
  try { localStorage.setItem(VISITED_KEY, new Date().toISOString().slice(0, 10)); } catch { /* storage off */ }
}

function load(locale: string) {
  if (!seenLoaded) {
    seenLoaded = true;
    const seen = readSeen();
    const firstVisit = seen === null && !priorVisit();
    markVisited();
    emit({ seen, firstVisit });
    // Another tab marking the release seen clears the dot here too.
    try {
      window.addEventListener("storage", (e) => {
        if (e.key === SEEN_KEY) emit({ seen: e.newValue });
      });
    } catch { /* no window events: this tab only */ }
  }
  if (loadedLocale === locale) return;
  loadedLocale = locale;
  fetch(`${API_BASE}/releases/latest?locale=${encodeURIComponent(locale)}`, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (loadedLocale !== locale) return;
      const latest = data ? toReleaseCard(data) : null;
      // First visit: the current release predates this visitor.
      if (latest && snapshot.firstVisit && snapshot.seen === null) markReleaseSeen(latest.id);
      emit({ latest, ready: true });
    })
    .catch(() => { if (loadedLocale === locale) emit({ latest: null, ready: true }); });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Record this release as seen (stored, and the dot goes everywhere). */
export function markReleaseSeen(id: number | string) {
  const k = String(id);
  if (snapshot.seen === k) return;
  try { localStorage.setItem(SEEN_KEY, k); } catch { /* storage off: this page load only */ }
  emit({ seen: k });
}

export interface UseLatestRelease {
  latest: ReleaseCard | null;
  ready: boolean;
  /** A latest release exists, this browser has not seen it, and it is
   *  not older than the signed-in account. */
  unseen: boolean;
  /** ``unseen`` and published in the last NEW_MARK_DAYS (7): the entries
   *  carry a dot. The age gate the corner card had, so an old release
   *  does not dot a browser forever. */
  dot: boolean;
  markSeen: () => void;
}

/** A release published before the account existed is not news to it. */
export function predatesAccount(latest: ReleaseCard | null, user: { created_at?: string | null } | null | undefined): boolean {
  if (!latest || !user?.created_at) return false;
  const created = Date.parse(user.created_at);
  const published = Date.parse(latest.published_at);
  return Number.isFinite(created) && Number.isFinite(published) && created > published;
}

export function useLatestRelease(user?: { created_at?: string | null } | null): UseLatestRelease {
  const { locale } = useLocale();
  const snap = useSyncExternalStore(subscribe, () => snapshot, () => SERVER_SNAPSHOT);
  useEffect(() => { load(locale); }, [locale]);
  const latest = snap.latest;
  const unseen = !!latest && snap.seen !== String(latest.id) && !predatesAccount(latest, user);
  const dot = unseen && !!latest && ageDays(latest.published_at) <= MAX_AGE_DAYS;
  const markSeen = useCallback(() => { if (latest) markReleaseSeen(latest.id); }, [latest]);
  return { latest, ready: snap.ready, unseen, dot, markSeen };
}
